import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faCheck, faTrophy, faBolt, faArrowLeft, faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { AREA_CONFIG } from './LevelUpHubPage';
import api from '../api/API';
import './MetasPage.css';

const emptyForm = () => ({
  titulo: '',
  descricao: '',
  area: 'carreira',
  tipo: 'anual',
  progresso: 0,
  xp_reward: 100,
});

const MetasPage = () => {
  const navigate = useNavigate();
  const [metas, setMetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedArea, setSelectedArea] = useState('carreira');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    const fetchMetas = async () => {
      try {
        setLoading(true);
        const res = await api.get('/pdi/metas');
        setMetas(res?.data?.records || []);
      } catch (err) {
        setError('Erro ao carregar metas.');
      } finally {
        setLoading(false);
      }
    };
    fetchMetas();
  }, []);

  const areaMetas = useMemo(() => metas.filter(m => m.area === selectedArea), [metas, selectedArea]);
  const anuais = useMemo(() => areaMetas.filter(m => m.tipo === 'anual'), [areaMetas]);
  const trimestrais = useMemo(() => areaMetas.filter(m => m.tipo === 'trimestral'), [areaMetas]);
  const mensais = useMemo(() => areaMetas.filter(m => m.tipo === 'mensal'), [areaMetas]);

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), area: selectedArea });
    setShowModal(true);
    setError('');
  };

  const handleOpenEdit = (meta) => {
    setEditingId(meta.id);
    setForm({
      titulo: meta.titulo,
      descricao: meta.descricao || '',
      area: meta.area,
      tipo: meta.tipo,
      progresso: meta.progresso || 0,
      xp_reward: meta.xp_reward || 100,
    });
    setShowModal(true);
    setError('');
  };

  const handleSave = useCallback(async () => {
    if (!form.titulo.trim()) return;
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        const res = await api.put(`/pdi/metas/${editingId}`, form);
        const updated = res?.data?.record;
        setMetas(prev => prev.map(m => m.id === editingId ? updated : m));
      } else {
        const res = await api.post('/pdi/metas', form);
        const created = res?.data?.record;
        setMetas(prev => [...prev, created]);
      }
      setShowModal(false);
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao salvar meta.');
    } finally {
      setSaving(false);
    }
  }, [form, editingId]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Excluir esta meta?')) return;
    try {
      await api.delete(`/pdi/metas/${id}`);
      setMetas(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      setError('Erro ao excluir meta.');
    }
  }, []);

  const handleToggle = useCallback(async (meta) => {
    const endpoint = meta.concluida
      ? `/pdi/metas/${meta.id}/reabrir`
      : `/pdi/metas/${meta.id}/concluir`;

    // Optimistic update
    setMetas(prev => prev.map(m => m.id === meta.id ? { ...m, concluida: !m.concluida } : m));

    try {
      const res = await api.patch(endpoint);
      const record = res?.data?.record;
      if (record) setMetas(prev => prev.map(m => m.id === meta.id ? record : m));
    } catch (err) {
      // Rollback
      setMetas(prev => prev.map(m => m.id === meta.id ? meta : m));
      setError('Erro ao atualizar meta.');
    }
  }, []);

  const selectedCfg = AREA_CONFIG.find(a => a.id === selectedArea) || AREA_CONFIG[0];

  const renderColumn = (title, list) => (
    <div>
      <div className="metas-column-title">
        {title}
        <span className="metas-column-count">{list.length}</span>
      </div>
      <div className="metas-card-list">
        {list.length === 0 ? (
          <div className="metas-empty" onClick={handleOpenAdd}>
            <FontAwesomeIcon icon={faTrophy} className="metas-empty-icon" />
            <p>Nenhuma meta {title.toLowerCase()}.</p>
            <span>Adicionar meta →</span>
          </div>
        ) : (
          list.map(meta => (
            <div
              key={meta.id}
              className={`metas-card${meta.concluida ? ' concluida' : ''}`}
              style={{ '--card-color': selectedCfg.color }}
            >
              <div className="metas-card-top">
                <div>
                  <p className="metas-card-title">{meta.titulo}</p>
                  {meta.descricao && <p className="metas-card-desc">{meta.descricao}</p>}
                </div>
                <div className="metas-card-actions">
                  <button
                    className="metas-card-btn success"
                    onClick={() => handleToggle(meta)}
                    title={meta.concluida ? 'Reabrir' : 'Concluir'}
                  >
                    <FontAwesomeIcon icon={meta.concluida ? faRotateLeft : faCheck} />
                  </button>
                  <button className="metas-card-btn" onClick={() => handleOpenEdit(meta)} title="Editar">
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button className="metas-card-btn danger" onClick={() => handleDelete(meta.id)} title="Excluir">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              <div className="metas-progress-wrap">
                <div className="metas-progress-labels">
                  <span>Progresso</span>
                  <strong>{meta.progresso || 0}%</strong>
                </div>
                <div className="metas-progress-bar">
                  <div className="metas-progress-fill" style={{ width: `${meta.progresso || 0}%` }} />
                </div>
              </div>
              {Number(meta.tarefas_total) > 0 && (
                <div className="metas-task-link">
                  <FontAwesomeIcon icon={faCheck} style={{ marginRight: 4, opacity: 0.7 }} />
                  {meta.tarefas_concluidas}/{meta.tarefas_total} tarefas vinculadas
                </div>
              )}
              {(meta.xp_reward || 0) > 0 && (
                <div className="metas-xp-badge">
                  <FontAwesomeIcon icon={faBolt} />
                  {meta.concluida ? `+${meta.xp_reward} XP conquistado` : `+${meta.xp_reward} XP ao concluir`}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="metas-page">
      <div className="metas-header">
        <div className="metas-header-left">
          <button
            style={{ background: 'transparent', border: 'none', color: 'rgba(226,232,240,0.5)', cursor: 'pointer', marginBottom: 8, fontSize: '0.84rem' }}
            onClick={() => navigate('/levelup')}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 6 }} />
            LevelUp Hub
          </button>
          <h1>Metas & Objetivos</h1>
          <p>Defina o que você quer conquistar em cada área da sua vida</p>
        </div>
        <button className="metas-add-btn" onClick={handleOpenAdd} disabled={saving}>
          <FontAwesomeIcon icon={faPlus} />
          Nova Meta
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', marginBottom: 16, fontSize: '0.88rem' }}>
          {error}
        </div>
      )}

      {/* Area tabs */}
      <div className="metas-area-tabs">
        {AREA_CONFIG.map(cfg => (
          <div
            key={cfg.id}
            className={`metas-area-tab${selectedArea === cfg.id ? ' active' : ''}`}
            style={selectedArea === cfg.id ? { background: `${cfg.color}22`, borderColor: `${cfg.color}55`, color: cfg.color } : {}}
            onClick={() => setSelectedArea(cfg.id)}
          >
            <div className="metas-area-tab-dot" style={{ background: cfg.color }} />
            <FontAwesomeIcon icon={cfg.icon} />
            {cfg.label}
            <span style={{ opacity: 0.6, fontSize: '0.72rem' }}>
              ({metas.filter(m => m.area === cfg.id && !m.concluida).length})
            </span>
          </div>
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'rgba(226,232,240,0.4)', textAlign: 'center', padding: '40px 0' }}>Carregando metas...</p>
      ) : (
        <>
          <div className="metas-content">
            {renderColumn('Anuais', anuais)}
            {renderColumn('Trimestrais', trimestrais)}
          </div>
          <div style={{ marginTop: 24 }}>
            {renderColumn('Mensais', mensais)}
          </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="metas-modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="metas-modal">
            <h2 className="metas-modal-title">
              {editingId ? 'Editar Meta' : 'Nova Meta'}
            </h2>

            <div className="metas-modal-field">
              <label className="metas-modal-label">Título *</label>
              <input
                className="metas-modal-input"
                value={form.titulo}
                onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                placeholder="Ex: Conquistar promoção para Sênior"
                autoFocus
              />
            </div>

            <div className="metas-modal-field">
              <label className="metas-modal-label">Descrição</label>
              <textarea
                className="metas-modal-textarea"
                value={form.descricao}
                onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                placeholder="Detalhes, contexto, por que isso importa..."
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="metas-modal-field">
                <label className="metas-modal-label">Área</label>
                <select
                  className="metas-modal-select"
                  value={form.area}
                  onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
                >
                  {AREA_CONFIG.map(a => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                </select>
              </div>
              <div className="metas-modal-field">
                <label className="metas-modal-label">Tipo</label>
                <select
                  className="metas-modal-select"
                  value={form.tipo}
                  onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                >
                  <option value="anual">Anual</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="mensal">Mensal</option>
                </select>
              </div>
            </div>

            <div className="metas-modal-field">
              <label className="metas-modal-label">Progresso: {form.progresso}%</label>
              <input
                type="range"
                className="metas-modal-range"
                min="0"
                max="100"
                value={form.progresso}
                onChange={e => setForm(f => ({ ...f, progresso: Number(e.target.value) }))}
              />
            </div>

            <div className="metas-modal-field">
              <label className="metas-modal-label">Recompensa XP ao concluir</label>
              <input
                type="number"
                className="metas-modal-input"
                value={form.xp_reward}
                min="0"
                max="5000"
                onChange={e => setForm(f => ({ ...f, xp_reward: Number(e.target.value) }))}
              />
            </div>

            {error && (
              <p style={{ color: '#fca5a5', fontSize: '0.84rem', margin: '0 0 12px' }}>{error}</p>
            )}

            <div className="metas-modal-footer">
              <button className="metas-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="metas-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Salvar' : 'Criar Meta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetasPage;
